import React, { Component, PureComponent } from 'react';

import { Button } from '@lightspeed/flame/Button';
import CirrusCheckbox from '@lightspeed/cirrus-checkbox';
import Group from '@lightspeed/cirrus-group';
import Input from '@lightspeed/cirrus-input';
import Select from '@lightspeed/cirrus-select';
import Table from 'cirrus/Table';
import Text, { TextLink } from '@lightspeed/cirrus-text';
import { Tooltip } from '@lightspeed/flame/Tooltip';
import { spacing } from '@lightspeed/cirrus-tokens/utils';
import { withFormik } from 'formik';
import get from 'lodash/get';
import map from 'lodash/map';
import some from 'lodash/some';
import sum from 'lodash/sum';
import times from 'lodash/times';
import truncate from 'lodash/truncate';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Format from 'helpers/format';
import TableSorter from 'pages/containers/TableSorter';
import { actionTypes } from 'pages/store/actions/defaults';
import { entities } from 'pages/store/schema';
import { getErrors, getValue } from 'pages/store/selectors';
import { formatNumberInput } from 'pages/utils/input';
import Modules from 'shared_modules/Modules';

import { formatMoney, keyExists } from './utils';
import PurchaseOrderSelect from './VendorReturnPurchaseOrderSelect';

const blurOnEnter = event => {
  if (event.key === 'Enter') {
    event.target.blur();
  }
};

/* eslint-disable react/no-multi-comp */

class Checkbox extends PureComponent {
  render() {
    return <CirrusCheckbox {...this.props} />;
  }
}

class BulkAction extends PureComponent {
  render() {
    const { checkedDataCount, isDeleting, onBulkActionClick } = this.props;
    return (
      <Group style={{ margin: '-6px 0' }}>
        <Text>
          <var>{checkedDataCount}</var> items selected
        </Text>
        <Button
          variant="danger"
          loading={isDeleting}
          disabled={isDeleting}
          onClick={onBulkActionClick}
          data-test="bulk-action-button"
        >
          Delete
        </Button>
      </Group>
    );
  }
}

class ItemDescriptionLink extends PureComponent {
  render() {
    const { itemDescription, itemID } = this.props;
    return (
      <Text className="notranslate" bodySmall bold element="span">
        <TextLink
          href={`/?name=item.views.item&form_name=view&id=${itemID}`}
          onClick={() => window.navigateToContent('view', 'item.views.item', itemID)}
          data-test="listing-description-link"
        >
          {itemDescription}
        </TextLink>
      </Text>
    );
  }
}

class ItemReasonSelect extends PureComponent {
  render() {
    const {
      selectedReason,
      itemID,
      index,
      reasons,
      updateVendorReturnItem,
      setFieldValue,
      disabled,
    } = this.props;

    if (!disabled) {
      const reasonOptions = reasons
        .filter(
          ({ vendorReturnItemReasonID, archived }) =>
            archived === false || selectedReason === vendorReturnItemReasonID
        )
        .map(({ vendorReturnItemReasonID, reason, archived, isSystem }) => (
          <option
            key={vendorReturnItemReasonID}
            value={vendorReturnItemReasonID}
            disabled={archived}
            className={!isSystem ? 'notranslate' : undefined}
          >
            {reason}
          </option>
        ));

      return (
        <Select
          onChange={({ target: { value } = {} } = {}) => {
            setFieldValue(`formItems[${index}].vendorReturnItemReasonID`, value);
            updateVendorReturnItem({
              itemID,
              payload: { vendorReturnItem: { vendorReturnItemReasonID: value } },
              onSuccess: () => window.flashNotifier('success', 'Item reason updated', 3),
              shouldUpdateVendorReturn: true,
            });
          }}
          value={selectedReason}
          data-test="listing-reason-select"
        >
          {reasonOptions}
        </Select>
      );
    }

    const selectedReasonValue = reasons.find(
      ({ vendorReturnItemReasonID }) => selectedReason === vendorReturnItemReasonID
    );
    return (
      <span data-test="listing-reason-text">
        {selectedReasonValue && (selectedReasonValue.reason || 'N/A')}
      </span>
    );
  }
}

class VendorNumberInput extends PureComponent {
  render() {
    const {
      disabled,
      initialVendorNumber,
      vendorNumber,
      index,
      itemID,
      updateVendorNumber,
      setFieldValue,
    } = this.props;

    return !disabled ? (
      <Input
        type="text"
        value={vendorNumber}
        style={{ textAlign: 'center' }}
        maxLength="255"
        onChange={({ target: { value } = {} } = {}) =>
          setFieldValue(`formItems[${index}].vendorNumber`, value)
        }
        onFocus={event => {
          window.selectField(event.target);
        }}
        onKeyDown={blurOnEnter}
        onBlur={({ target: { value } = {} } = {}) => {
          if (initialVendorNumber !== value) {
            updateVendorNumber({
              itemID,
              payload: { vendorNumber: { value } },
              success: window.flashNotifier('success', 'Item vendor ID updated', 3),
            });
          }
        }}
        data-test="listing-vendor-number-input"
      />
    ) : (
      <span data-test="listing-vendor-number-text" className="notranslate">
        {vendorNumber}
      </span>
    );
  }
}

const Stock = ({ stock }) => <span>{truncate(stock, { length: 10 })}</span>;

class QtyInput extends PureComponent {
  render() {
    const {
      qty,
      initialQty,
      index,
      itemID,
      isOverReturn,
      updateVendorReturnItem,
      setFieldValue,
      disabled,
    } = this.props;

    return !disabled ? (
      <Tooltip
        content="Over return"
        className={!isOverReturn ? 'cr-display-none' : ''}
        placement="bottom-end"
      >
        <Input
          type="number"
          value={qty}
          style={{ textAlign: 'center' }}
          status={isOverReturn ? { type: 'error' } : {}}
          onChange={({ target: { value } = {} } = {}) =>
            setFieldValue(`formItems[${index}].quantity`, value)
          }
          onFocus={event => {
            window.selectField(event.target);
          }}
          onKeyDown={blurOnEnter}
          onBlur={({ target: { value } = {} } = {}) => {
            let formattedQty = parseInt(value, 10);
            if (!formattedQty || formattedQty <= 0) {
              formattedQty = initialQty;
            }
            if (formattedQty !== initialQty) {
              updateVendorReturnItem({
                itemID,
                payload: { vendorReturnItem: { quantity: formattedQty } },
                onSuccess: () => window.flashNotifier('success', 'Item quantity updated', 3),
                shouldUpdateVendorReturn: true,
              });
            }
            setFieldValue(`formItems[${index}].quantity`, formattedQty);
          }}
          data-test="listing-qty-input"
        />
      </Tooltip>
    ) : (
      <span data-test="listing-qty-text">{qty}</span>
    );
  }
}

class CostInput extends PureComponent {
  render() {
    const {
      cost,
      initialCost,
      index,
      itemID,
      currencySymbol,
      updateVendorReturnItem,
      setFieldValue,
      disabled,
    } = this.props;

    return !disabled ? (
      <Input
        type="text"
        value={cost}
        style={{ textAlign: 'right' }}
        prefix={<span className="notranslate">{currencySymbol}</span>}
        disabled={disabled}
        onFocus={event => {
          window.selectField(event.target);
        }}
        onChange={({ target: { value } = {} } = {}) =>
          setFieldValue(`formItems[${index}].cost`, formatNumberInput(value))
        }
        onKeyDown={blurOnEnter}
        onBlur={({ target: { value } = {} } = {}) => {
          let formattedCost = value && formatMoney(value);
          if (!formattedCost || formattedCost < 0) {
            formattedCost = initialCost;
          }
          if (formattedCost !== initialCost) {
            updateVendorReturnItem({
              itemID,
              payload: { vendorReturnItem: { cost: { value: formattedCost } } },
              onSuccess: () => window.flashNotifier('success', 'Item cost updated', 3),
              shouldUpdateVendorReturn: true,
            });
          }
          setFieldValue(`formItems[${index}].cost`, formattedCost);
        }}
        data-test="listing-cost-input"
      />
    ) : (
      <span data-test="listing-cost-text" className="notranslate">
        {currencySymbol + cost}
      </span>
    );
  }
}

const updateVendorReturnItemTaxRate = (
  updateVendorReturnItemTax,
  itemID,
  vendorReturnItemTaxID
) => newTaxRate => {
  updateVendorReturnItemTax({
    itemID,
    vendorReturnItemTaxID,
    newTaxRate,
    onSuccess: () => window.flashNotifier('success', 'Vendor Return Item Tax rate updated', 3),
    shouldUpdateVendorReturn: true,
  });
};

export const moveDecimalDelimiter = (stringValue, offset) => {
  const value = stringValue.indexOf('.') === -1 ? parseFloat(stringValue).toFixed(2) : stringValue;

  const delimiterPosition = value.indexOf('.');
  const newDelimiterPosition = delimiterPosition + offset;
  const valueWithoutDelimiter = value.split('.').join('');

  let percentValue;
  if (newDelimiterPosition <= 0) {
    const padding = times(Math.abs(newDelimiterPosition), () => '0').join('');
    percentValue = `0.${padding}${valueWithoutDelimiter}`;
  } else if (newDelimiterPosition > valueWithoutDelimiter.length) {
    const padding = times(newDelimiterPosition - valueWithoutDelimiter.length, () => '0').join('');
    percentValue = `${valueWithoutDelimiter}${padding}`;
  } else {
    percentValue = `${valueWithoutDelimiter.slice(
      0,
      newDelimiterPosition
    )}.${valueWithoutDelimiter.slice(newDelimiterPosition)}`;
  }

  // Trim heading 0
  if (percentValue !== '0') {
    percentValue = percentValue.replace(/^0+/, '');

    // Add a heading '0' in case we are stuck with a number that starts with a '.'
    if (percentValue[0] === '.') {
      percentValue = `0${percentValue}`;
    }
  }

  // Trim trailing 0 if we have fractions
  if (percentValue.indexOf('.') !== -1) {
    percentValue = percentValue.replace(/0+$/, '');

    // Remove trailing '.'
    if (percentValue[percentValue.length - 1] === '.') {
      percentValue = percentValue.slice(0, -1);
    }
  }

  return percentValue;
};

const decimalToPercent = decimalValue => {
  return moveDecimalDelimiter(`${decimalValue}`, 2);
};
// Convert percent values to decimal without using float point arithmetic to prevent precision problems
const percentToDecimal = decimalValue => {
  return moveDecimalDelimiter(`${decimalValue}`, -2);
};

class TaxRateInput extends Component {
  shouldComponentUpdate(nextProps) {
    return (
      this.props.taxRate !== nextProps.taxRate ||
      this.props.disabled !== nextProps.disabled ||
      this.props.vendorReturnItemTaxID !== nextProps.vendorReturnItemTaxID ||
      this.props.index !== nextProps.index
    );
  }

  render() {
    const {
      disabled,
      index,
      initialRate,
      onRateChange,
      setFieldValue,
      taxRate,
      vendorReturnItemTaxID,
    } = this.props;
    // const percentTaxRate = decimalToPercent(taxRate);
    const percentTaxRate = taxRate;

    return !disabled ? (
      <Input
        type="number"
        value={percentTaxRate}
        style={{ textAlign: 'right' }}
        disabled={disabled}
        prefix={<span className="notranslate">%</span>}
        onFocus={event => {
          window.selectField(event.target);
        }}
        onChange={event => {
          const percentValue = event.target.value;
          // Reject non-number values
          if (isNaN(percentValue)) {
            return;
          }

          setFieldValue(`formItems[${index}].taxes[${vendorReturnItemTaxID}].rate`, percentValue);
        }}
        onKeyDown={blurOnEnter}
        onBlur={event => {
          const percentValue = event.target.value;

          // Replace all non-number values by 0
          if (isNaN(percentValue)) {
            setFieldValue(`formItems[${index}].taxes[${vendorReturnItemTaxID}].rate`, 0);
            return;
          }

          const decimalRate = percentToDecimal(formatNumberInput(percentValue));
          if (decimalRate !== initialRate) {
            onRateChange(decimalRate);
          }
          setFieldValue(`formItems[${index}].taxes[${vendorReturnItemTaxID}].rate`, percentValue);
        }}
        data-test="listing-taxRate-input"
      />
    ) : (
      <span data-test="listing-taxRate-text" className="notranslate">
        %{truncate(percentTaxRate, { length: 12 })}
      </span>
    );
  }
}

const TaxAmount = ({ taxAmount }) => (
  <span data-test="listing-taxAmount-text" className="notranslate">
    {Format.toMoney(taxAmount)}
  </span>
);

const Subtotal = ({ subtotal }) => (
  <span data-test="listing-subtotal-text" className="notranslate">
    {Format.toMoney(subtotal)}
  </span>
);

const getTaxInclusive = (totalTaxRate, taxRate, cost) => {
  if (totalTaxRate === 0 || taxRate === 0) {
    return 0;
  }

  return (cost - cost / (1 + totalTaxRate)) * (taxRate / totalTaxRate);
};

/**
 * Calculate VR Item TaxAmount base on TaxInclusive or TaxExclusive:
 *
 * When TaxInclusive, cost divided by 1 + totalRate will give you
 * the net cost without tax, then subtract from cost will give you
 * the total taxAmount. Finally, in order to get the taxAmount for each taxRate,
 * you need to use the total taxAmount multiply by
 * the current tax rate as a percentage of the total tax rate.
 *
 * When TaxExclusive, simply use the cost multiply by
 * each taxRate will give you the taxAmount.
 *
 * Tax rate will be converted from decimal to %percentage
 */
export const extractTax = (cost, quantity, totalTaxRate, isTaxInclusive = false) => ({
  taxRate,
  vendorReturnItemTaxID,
}) => ({
  vendorReturnItemTaxID,
  amount:
    (isTaxInclusive ? getTaxInclusive(totalTaxRate, taxRate, cost) : cost * taxRate) * quantity,
  rate: decimalToPercent(taxRate),
});

const getTaxesHead = (hasItems, hasItemKey, item) => {
  /**
   * Regardless of the data, only show taxes if the module is present
   */
  if (!Modules.hasModule('ordering.item.taxes')) {
    return [];
  }

  if (!hasItemKey('Taxes') || !item.Taxes.data.length) {
    return [];
  }

  const taxes = (item?.Taxes?.data ?? []).map(
    ({ taxName }, index) => (taxName ?? `Tax ${index + 1}`) || `Tax ${index + 1}`
  );
  return taxes
    .map((taxName, index) => {
      const rate = {
        key: `taxRate-${index}`,
        content: hasItems ? (
          <TableSorter sortKey="taxRate" entity={entities.vendorReturnItem}>
            {`${taxName} Rate`}
          </TableSorter>
        ) : (
          `Tax Rate ${index}`
        ),
        align: 'right',
        width: '8rem',
      };

      const amount = {
        key: `taxAmount-${index}`,
        content: hasItems ? (
          <TableSorter sortKey="taxAmount" entity={entities.vendorReturnItem}>
            {`${taxName} Amount`}
          </TableSorter>
        ) : (
          `Tax Amount ${index}`
        ),
        align: 'right',
        width: '9rem',
      };

      return [rate, amount];
    })
    .flatten();
};

const getResetTaxButton = (hasItemKey, item, resetVendorReturnItemTax) => {
  /**
   * Regardless of the data, only show taxes if the module is present
   */
  if (!Modules.hasModule('ordering.item.taxes')) {
    return [];
  }

  const anItemHasTaxes = !!item.taxes;
  const isResetButtonEnabled = item.isTaxRateEdited && hasItemKey('Taxes') && anItemHasTaxes;

  return [
    {
      key: `reset-vendor-return-item-tax-button-${item.vendorReturnItemID}`,
      style: {
        width: '9rem',
        verticalAlign: 'middle',
      },
      content: isResetButtonEnabled ? (
        <Button
          onClick={() => {
            resetVendorReturnItemTax({ itemID: item.itemID });
          }}
        >
          Reset
        </Button>
      ) : (
        <Button disabled="disabled">Reset</Button>
      ),
      className: 'cr-text-right notranslate ',
    },
  ];
};

const getResetTaxHead = (hasItems, hasItemKey, items) => {
  /**
   * Regardless of the data, only show taxes if the module is present
   */
  if (!Modules.hasModule('ordering.item.taxes')) {
    return [];
  }

  const isTaxResetColumnVisible = items.some(item => item?.Taxes?.data?.length > 0);

  if (!hasItemKey('Taxes') || !isTaxResetColumnVisible) {
    return [];
  }

  return [
    {
      key: `reset-vendor-return-item-tax-head-${items[0].vendorReturnID}`,
      content: 'Tax Rate',
      width: '6.5rem',
      align: 'center',
    },
  ];
};

const getTableHead = ({
  hasItems,
  updateItem,
  vendorID,
  isDeleting,
  isAllChecked,
  checkedDataCount,
  handleAllChange,
  handleBulkActionClick,
  items,
}) => {
  // all items will have the same response structure
  const [firstItem = {}] = items;
  const hasItemKey = keyExists(firstItem);

  const columns = [
    hasItemKey('addOrder') && {
      key: 'addOrder',
      content: hasItems ? (
        <TableSorter sortKey="addOrder" entity={entities.vendorReturnItem}>
          #
        </TableSorter>
      ) : (
        '#'
      ),
      width: '3rem',
    },
    hasItemKey('Item') && {
      key: 'description',
      content: hasItems ? (
        <TableSorter sortKey="description" entity={entities.vendorReturnItem}>
          Item
        </TableSorter>
      ) : (
        'Item'
      ),
      width: '8rem',
    },
    hasItemKey('orderID') && {
      key: 'orderID',
      content: hasItems ? (
        <TableSorter sortKey="orderID" entity={entities.vendorReturnItem}>
          PO
        </TableSorter>
      ) : (
        'PO'
      ),
      width: '8rem',
    },
    hasItemKey('vendorReturnItemReasonID') && {
      key: 'reason',
      content: hasItems ? (
        <TableSorter sortKey="reason" entity={entities.vendorReturnItem}>
          Reason
        </TableSorter>
      ) : (
        'Reason'
      ),
      width: '8rem',
    },
    vendorID
      ? {
          key: 'vendorID',
          content: 'Vendor ID',
          width: '7.6rem',
        }
      : undefined,
    hasItemKey('Item') && {
      key: 'qoh',
      content: hasItems ? (
        <TableSorter sortKey="qoh" entity={entities.vendorReturnItem}>
          Stock
        </TableSorter>
      ) : (
        'Stock'
      ),
      width: '6.5rem',
      align: 'center',
    },
    hasItemKey('quantity') && {
      key: 'quantity',
      content: hasItems ? (
        <TableSorter sortKey="quantity" entity={entities.vendorReturnItem}>
          Return qty.
        </TableSorter>
      ) : (
        'Return Qty.'
      ),
      width: '7.5rem',
      align: 'center',
    },
    hasItemKey('cost') && {
      key: 'cost',
      content: hasItems ? (
        <TableSorter sortKey="cost" entity={entities.vendorReturnItem}>
          Cost
        </TableSorter>
      ) : (
        'Cost'
      ),
      width: '7rem',
      align: 'right',
    },
    ...getTaxesHead(hasItems, hasItemKey, firstItem),
    hasItemKey('subtotal') && {
      key: 'subtotal',
      content: hasItems ? (
        <TableSorter sortKey="subtotal" entity={entities.vendorReturnItem}>
          Subtotal
        </TableSorter>
      ) : (
        'Subtotal'
      ),
      align: 'right',
      width: '8rem',
    },
    ...getResetTaxHead(hasItems, hasItemKey, items),
  ];

  return {
    cells: [
      updateItem
        ? {
            key: 'select',
            content: (
              <Checkbox
                disabled={!hasItems}
                checked={isAllChecked}
                onChange={handleAllChange}
                data-test="bulk-action-all-items-input"
              />
            ),
            width: '2.2rem',
          }
        : undefined,
      (updateItem && checkedDataCount) || isDeleting
        ? {
            key: 'bulkAction',
            content: (
              <BulkAction
                checkedDataCount={checkedDataCount}
                isDeleting={isDeleting}
                onBulkActionClick={handleBulkActionClick}
              />
            ),
            colSpan: columns.length,
          }
        : undefined,
    ]
      .concat(updateItem && checkedDataCount ? [] : columns)
      .filter(e => e),
  };
};

const getTaxesBody = (
  hasItemKey,
  item,
  currencySymbol,
  updateVendorReturnItemTax,
  disabled,
  vendorReturnItemIndex,
  setFieldValue,
  initialTaxes
) => {
  /**
   * Regardless of the data, only show taxes if the module is present
   */
  if (!Modules.hasModule('ordering.item.taxes')) {
    return [];
  }

  if (!hasItemKey('Taxes')) {
    return [];
  }

  return Object.values(item.taxes)
    .map(tax => {
      const rate = {
        key: `taxRate-${tax.vendorReturnItemTaxID}`,
        style: {
          width: '8rem',
          verticalAlign: 'middle',
        },
        content: (
          <TaxRateInput
            disabled={disabled}
            index={vendorReturnItemIndex}
            setFieldValue={setFieldValue}
            vendorReturnItemTaxID={tax.vendorReturnItemTaxID}
            initialRate={initialTaxes[tax.vendorReturnItemTaxID].rate}
            taxRate={tax.rate}
            onRateChange={updateVendorReturnItemTaxRate(
              updateVendorReturnItemTax,
              item.itemID,
              tax.vendorReturnItemTaxID
            )}
          />
        ),
        className: 'cr-text-right notranslate ',
      };

      const amount = {
        key: `taxAmount-${tax.vendorReturnItemTaxID}`,
        style: {
          width: '9rem',
          verticalAlign: 'middle',
        },
        content: <TaxAmount taxAmount={tax.amount} />,
        className: 'cr-text-right notranslate ',
      };
      return [rate, amount];
    })
    .flatten();
};

const getTableBody = ({
  isArchived,
  vendorID,
  items,
  formItems,
  initialFormItems,
  reasons,
  updateVendorReturnItem,
  updateVendorNumber,
  setFieldValue,
  updateItem,
  vendorReturnID,
  checkedData,
  handleSingleChange,
  updateVendorReturnItemTax,
  resetVendorReturnItemTax,
}) => {
  const [firstItem = {}] = items;
  const hasItemKey = keyExists(firstItem);
  return formItems.length
    ? map(formItems, (item, index) => {
        const isOverReturn = updateItem && !!item.status;
        const currencySymbol = get(window, 'merchantos.locale.currency_symbol', '');

        return {
          key: item.itemID,
          error: isOverReturn,
          cells: [
            updateItem
              ? {
                  key: 'select',
                  content: (
                    <Checkbox
                      // eslint-disable-next-line
                  checked={some(checkedData, (value, key) => key === item.itemID)}
                      onChange={e => handleSingleChange(e, items[index])}
                      data-test={`bulk-action-item-${index}-input`}
                    />
                  ),
                  style: {
                    verticalAlign: 'middle',
                  },
                }
              : undefined,
            hasItemKey('addOrder') && {
              key: 'addOrder',
              style: {
                width: '3rem',
                verticalAlign: 'middle',
              },
              content: item.addOrder,
            },
            hasItemKey('Item') && {
              key: 'description',
              content: (
                <ItemDescriptionLink itemDescription={item.description} itemID={item.itemID} />
              ),
              className: 'notranslate',
              style: {
                verticalAlign: 'middle',
                paddingTop: spacing(1),
                paddingBottom: spacing(1),
                lineHeight: spacing(2),
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                width: '6.5rem',
              },
            },
            hasItemKey('orderID') && {
              key: 'orderID',
              style: {
                width: '8rem',
                verticalAlign: 'middle',
                paddingTop: spacing(1),
                paddingBottom: spacing(1),
              },
              content: (
                <PurchaseOrderSelect
                  purchaseOrder={item.orderID}
                  vendorReturnID={vendorReturnID}
                  vendorReturnItemID={item.vendorReturnItemID}
                  itemID={item.itemID}
                  vendorID={vendorID}
                  disabled={!updateItem}
                  updateVendorReturnItem={updateVendorReturnItem}
                />
              ),
            },
            hasItemKey('vendorReturnItemReasonID') && {
              key: 'reason',
              style: {
                width: '8rem',
                verticalAlign: 'middle',
                paddingTop: spacing(1),
                paddingBottom: spacing(1),
              },
              content: (
                <ItemReasonSelect
                  selectedReason={item.vendorReturnItemReasonID}
                  reasons={reasons}
                  itemID={item.itemID}
                  index={index}
                  updateVendorReturnItem={updateVendorReturnItem}
                  setFieldValue={setFieldValue}
                  disabled={!updateItem}
                />
              ),
            },
            vendorID
              ? {
                  key: 'vendorID',
                  style: {
                    width: '7.6rem',
                    verticalAlign: 'middle',
                    paddingTop: spacing(1),
                    paddingBottom: spacing(1),
                  },
                  content: (
                    <VendorNumberInput
                      disabled={isArchived}
                      vendorNumber={item.vendorNumber}
                      initialVendorNumber={initialFormItems[index].vendorNumber}
                      index={index}
                      itemID={item.itemID}
                      updateVendorNumber={updateVendorNumber}
                      setFieldValue={setFieldValue}
                    />
                  ),
                }
              : undefined,
            hasItemKey('Item') && {
              key: 'qoh',
              style: {
                width: '6.5rem',
                verticalAlign: 'middle',
              },
              content: <Stock stock={item.qoh} />,
              className: 'cr-text-center',
            },
            hasItemKey('quantity') && {
              key: 'quantity',
              style: {
                width: '7.5rem',
                verticalAlign: 'middle',
                paddingTop: spacing(1),
                paddingBottom: spacing(1),
              },
              content: (
                <QtyInput
                  qty={item.quantity}
                  initialQty={initialFormItems[index].quantity}
                  index={index}
                  isOverReturn={isOverReturn}
                  itemID={item.itemID}
                  updateVendorReturnItem={updateVendorReturnItem}
                  setFieldValue={setFieldValue}
                  disabled={!updateItem}
                />
              ),
            },
            hasItemKey('cost') && {
              key: 'cost',
              style: {
                width: '7rem',
                verticalAlign: 'middle',
                paddingTop: spacing(1),
                paddingBottom: spacing(1),
              },
              content: (
                <CostInput
                  cost={item.cost}
                  initialCost={initialFormItems[index].cost}
                  itemID={item.itemID}
                  index={index}
                  currencySymbol={currencySymbol}
                  updateVendorReturnItem={updateVendorReturnItem}
                  setFieldValue={setFieldValue}
                  disabled={!updateItem}
                />
              ),
            },
            ...getTaxesBody(
              hasItemKey,
              item,
              currencySymbol,
              updateVendorReturnItemTax,
              !updateItem,
              index,
              setFieldValue,
              initialFormItems[index].taxes
            ),
            hasItemKey('subtotal') && {
              key: 'subtotal',
              style: {
                width: '8rem',
                verticalAlign: 'middle',
              },
              content: <Subtotal subtotal={item.subtotal} />,
              className: 'cr-text-right notranslate',
            },
            ...getResetTaxButton(hasItemKey, item, resetVendorReturnItemTax),
          ].filter(e => e),
        };
      })
    : [];
};

class VendorReturnItemListingForm extends Component {
  componentDidUpdate(prevProps) {
    const { isError, resetForm } = this.props;
    if (!prevProps.isError && isError) {
      resetForm();
    }
  }

  render() {
    const {
      isArchived,
      vendorID,
      items,
      itemsExist,
      reasons,
      isLoading,
      dataInit,
      updateVendorReturnItem,
      updateVendorNumber,
      setFieldValue,
      values: { formItems } = {},
      initialValues: { formItems: initialFormItems } = {},
      updateItem,
      vendorReturnID,
      isDeleting,
      isAllChecked,
      checkedData,
      checkedDataCount,
      handleAllChange,
      handleSingleChange,
      handleBulkActionClick,
      updateVendorReturnItemTax,
      resetVendorReturnItemTax,
    } = this.props;

    return (
      <fieldset disabled={isLoading || isDeleting}>
        <Table
          head={getTableHead({
            hasItems: !!items.length,
            updateItem,
            vendorID,
            isDeleting,
            isAllChecked,
            checkedDataCount,
            handleAllChange,
            handleBulkActionClick,
            items,
          })}
          body={getTableBody({
            isArchived,
            vendorID,
            items,
            formItems,
            initialFormItems,
            reasons,
            updateVendorReturnItem,
            updateVendorNumber,
            setFieldValue,
            updateItem,
            vendorReturnID,
            checkedData,
            handleSingleChange,
            updateVendorReturnItemTax,
            resetVendorReturnItemTax,
          })}
          isLoading={isLoading || isDeleting}
          dataInit={dataInit}
          noDataMessage={itemsExist ? 'No items were found.' : 'No items on vendor return.'}
        />
      </fieldset>
    );
  }
}

const arrayToObjectByKey = key => (acc, current) => {
  acc[current[key]] = current;
  return acc;
};

const formSettings = {
  enableReinitialize: true,
  mapPropsToValues: ({ items, vendorReturn }) => ({
    formItems: items.map(
      ({
        Item,
        vendorReturnItemID,
        addOrder,
        orderID,
        vendorReturnItemReasonID,
        quantity,
        cost: { value: cost } = {},
        subtotal: { value: subtotal } = {},
        isTaxRateEdited,
        Taxes,
      }) => ({
        vendorReturnItemID,
        itemID: get(Item, 'data.itemID', '').toString(),
        description: get(Item, 'data.description', ''),
        qoh: get(Item, 'data.qoh', ''),
        status: get(Item, 'data.status', ''),
        vendorNumber: get(Item, 'data.vendorNumber', ''),
        addOrder,
        orderID,
        vendorReturnItemReasonID,
        quantity,
        cost: formatMoney(cost),
        subtotal: formatMoney(subtotal),
        isTaxRateEdited,
        taxes: (Taxes?.data ?? [])
          .map(
            extractTax(
              cost,
              quantity,
              sum((Taxes?.data ?? []).map(({ taxRate }) => taxRate)),
              vendorReturn?.Shop?.data?.TaxCategory?.data?.isTaxInclusive ?? false
            )
          )
          .reduce(arrayToObjectByKey('vendorReturnItemTaxID'), {}),
      })
    ),
  }),
  handleSubmit: () => {},
};

const mapStateToProps = (state, ownProps) => ({
  vendorReturn: getValue(entities.vendorReturns, ownProps.vendorReturnID)(state),
  isError: getErrors({
    entity: entities.vendorReturnItem,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
});

const ConnectedVendorReturnItemListingForm = connect(
  mapStateToProps,
  null
)(withFormik(formSettings)(VendorReturnItemListingForm));

ConnectedVendorReturnItemListingForm.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape()),
  reasons: PropTypes.arrayOf(PropTypes.shape()),
  isLoading: PropTypes.bool,
  dataInit: PropTypes.bool,
  updateVendorReturnItem: PropTypes.func,
  updateVendorReturnItemTax: PropTypes.func,
  resetVendorReturnItemTax: PropTypes.func,
  updateItem: PropTypes.bool,
  vendorReturnID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  vendorID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ConnectedVendorReturnItemListingForm;
